// pages/PowerPaymentDashboard.jsx
// Admin view of power_event_bookings — one row per company booking.

import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import API from '../api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { FiZap, FiCheckCircle, FiClock, FiXCircle, FiRefreshCw, FiCopy, FiExternalLink, FiChevronDown, FiChevronUp, FiDownload, FiTrash2, FiEdit2, FiPlus } from 'react-icons/fi';

const fmt   = (n)  => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDt = (dt) => dt ? new Date(dt).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

const STATUS = {
  SUCCESS: { bg: 'rgba(0,214,143,.12)',  color: '#00a86b', label: '✅ Paid'     },
  PENDING: { bg: 'rgba(245,166,35,.12)', color: '#d4900a', label: '⏳ Pending'  },
  FAILED:  { bg: 'rgba(255,71,87,.12)',  color: '#c0392b', label: '❌ Failed'   },
  REFUNDED:{ bg: 'rgba(100,100,200,.12)',color: '#5555aa', label: '↩ Refunded' },
};

const dayBreakdownArray = (b) => {
  let days = b.day_breakdown;
  if (typeof days === 'string') {
    try { days = JSON.parse(days); } catch { days = null; }
  }
  return Array.isArray(days) ? days : [];
};

const EXPORT_FIELDS = [
  { key: 'date',        label: 'Date',                get: b => fmtDt(b.created_at), default: true },
  { key: 'txnId',       label: 'Transaction ID',       get: b => b.merchant_transaction_id, default: true },
  { key: 'company',     label: 'Company Name',         get: b => b.company_name, default: true },
  { key: 'boothNo',     label: 'Booth No.',            get: b => b.booth_no || '', default: true },
  { key: 'boothName',   label: 'Booth Name',           get: b => b.booth_name || '', default: true },
  { key: 'gst',         label: 'GST Number',           get: b => b.company_gst || '', default: true },
  { key: 'phone',       label: 'Phone',                get: b => b.company_phone, default: true },
  { key: 'email',       label: 'Email',                get: b => b.company_email || '', default: true },
  { key: 'address',     label: 'Address',              get: b => b.company_address || '', default: false },
  { key: 'eventName',   label: 'Event Name',           get: b => b.event_name || '', default: false },
  { key: 'days',        label: 'Power Days (count)',   get: b => dayBreakdownArray(b).length, default: true },
  // { key: 'dayDetail',   label: 'Power Day Breakdown',  get: b => dayBreakdownArray(b)
  //     .map(d => `Day ${d.day} (${d.date}): ${d.kv}kV - ₹${d.total}`).join(' | '), default: false },
 { key: 'dayDetail',   label: 'Day-wise breakdown (separate columns)', get: null, default: false },
  { key: 'subtotal',    label: 'Subtotal',             get: b => b.subtotal ?? '', default: false },
  { key: 'gstAmount',   label: 'GST Amount',           get: b => b.gst_amount ?? '', default: false },
  { key: 'total',       label: 'Total Amount',         get: b => b.total_amount, default: true },
  { key: 'status',      label: 'Payment Status',       get: b => b.payment_status, default: true },
  { key: 'razorpayId',  label: 'Razorpay Payment ID',  get: b => b.razorpay_payment_id || b.txn_razorpay_payment_id || '', default: false },
  { key: 'paymentLink', label: 'Payment Link',         get: b => b.payment_link || '', default: false },
];

function Chip({ status }) {
  const s = STATUS[status] || STATUS.PENDING;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

const VERIFICATION = {
  PENDING:   { bg: 'rgba(176,176,192,.15)', color: '#6b6b80', label: '— Not submitted' },
  SUBMITTED: { bg: 'rgba(245,166,35,.12)',  color: '#d4900a', label: '⏳ Awaiting verification' },
  VERIFIED:  { bg: 'rgba(0,214,143,.12)',   color: '#00a86b', label: '✅ Verified' },
  REJECTED:  { bg: 'rgba(255,71,87,.12)',   color: '#c0392b', label: '❌ Rejected' },
};

function VerificationChip({ status }) {
  const s = VERIFICATION[status] || VERIFICATION.PENDING;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:14, padding:'18px 22px', display:'flex', alignItems:'center', gap:16 }}>
      <div style={{ width:46, height:46, borderRadius:12, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, color, flexShrink:0 }}>{icon}</div>
      <div>
        <div style={{ fontSize:22, fontWeight:800, color:'#1a1a2e', lineHeight:1 }}>{value}</div>
        <div style={{ fontSize:12, color:'#6b6b80', marginTop:3 }}>{label}</div>
        {sub && <div style={{ fontSize:12, fontWeight:600, color, marginTop:2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function DayBreakdown({ raw }) {
  if (!raw) return <span style={{ color:'#b0b0c0' }}>—</span>;
  let days = raw;
  if (typeof raw === 'string') { try { days = JSON.parse(raw); } catch { return <span style={{ color:'#b0b0c0' }}>—</span>; } }
  if (!Array.isArray(days)) return <span style={{ color:'#b0b0c0' }}>—</span>;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      {days.map((d, i) => (
        <div key={i} style={{ fontSize:11, display:'flex', gap:8, alignItems:'center' }}>
          <span style={{ fontWeight:700, color:'#1a1a2e', minWidth:36 }}>Day {d.day}</span>
          <span style={{ color:'#6b6b80' }}>{d.date}</span>
          <span style={{ fontWeight:700, color:'#f5a623' }}>{d.kv} kV</span>
          <span style={{ color:'#4a4a5e' }}>{fmt(d.total)}</span>
          {d.surcharge > 0 && <span style={{ fontSize:10, background:'rgba(255,71,87,.1)', color:'#ff4757', borderRadius:6, padding:'1px 6px' }}>3-way +{fmt(d.surcharge)}</span>}
        </div>
      ))}
    </div>
  );
}

// ── Edit form field helpers ──────────────────────────────────────────────
const fieldStyle = {
  width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8,
  border: '1px solid #dcdce6', fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

function Field({ label, value, onChange, type = 'text', span }) {
  return (
    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#4a4a5e', gridColumn: span ? 'span 2' : undefined }}>
      {label}
      <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} style={fieldStyle} />
    </label>
  );
}

let dayRowSeq = 0;
const newDayRow = () => ({ _key: `d${Date.now()}_${dayRowSeq++}`, day: '', date: '', kv: '', total: '', surcharge: '' });

function bookingToEditForm(b) {
  const rawDays = dayBreakdownArray(b);
  const days = rawDays.length
    ? rawDays.map(d => ({
        _key: `d${Date.now()}_${dayRowSeq++}`,
        day: d.day ?? '',
        date: d.date ?? '',
        kv: d.kv ?? '',
        total: d.total ?? '',
        surcharge: d.surcharge ?? '',
      }))
    : [newDayRow()];

  return {
    merchantTransactionId: b.merchant_transaction_id,

    boothNo: b.booth_no || '',
    boothName: b.booth_name || '',

    companyName: b.company_name || '',
    companyGst: b.company_gst || '',
    companyAddress: b.company_address || '',
    companyEmail: b.company_email || '',
    companyPhone: b.company_phone || '',

    days,

    subtotal: b.subtotal ?? '',
    gstAmount: b.gst_amount ?? '',
    totalAmount: b.total_amount ?? '',
  };
}

export default function PowerPaymentDashboard() {
  const [stats,   setStats]   = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('ALL');
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);

  // ── Delete: selection + in-flight state ─────────────────────────────────
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // ── Edit: modal + form state ─────────────────────────────────────────────
  const [editForm, setEditForm] = useState(null); // null = modal closed
  const [savingEdit, setSavingEdit] = useState(false);
  const setEditField = (key, value) => setEditForm(prev => ({ ...prev, [key]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/razorpay/power-payment-stats');
      if (!data.success) throw new Error(data.message);
      setStats(data.stats);
      setBookings(data.bookings || []);
    } catch (err) {
      toast.error('Failed to load power payment data');
    } finally {
      setLoading(false);
    }
  }, []);

  const [syncing,  setSyncing]  = useState(false);
  const [syncMsg,  setSyncMsg]  = useState('');
  const [marking,  setMarking]  = useState(null);

  const syncAll = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const { data } = await API.post('/razorpay/sync-power-statuses');
      if (!data.success) throw new Error(data.message);
      const updated = data.results?.filter(r => r.newStatus === 'SUCCESS').length || 0;
      setSyncMsg(updated > 0
        ? `✅ ${updated} payment${updated > 1 ? 's' : ''} marked as paid`
        : `✓ Checked ${data.checked} booking${data.checked !== 1 ? 's' : ''} — all up to date`
      );
      if (updated > 0) await load();
    } catch (err) {
      setSyncMsg('❌ Sync failed — ' + (err.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const markAsPaid = async (b) => {
    const paymentId = window.prompt(
      `Enter the Razorpay Payment ID from the Razorpay dashboard\n(e.g. pay_T4dsthgF9qIkig)\n\nLeave blank to mark paid without a payment ID:`,
      ''
    );
    if (paymentId === null) return;
    setMarking(b.merchant_transaction_id);
    try {
      const { data } = await API.post('/razorpay/mark-power-paid', {
        merchantTransactionId: b.merchant_transaction_id,
        razorpayPaymentId: paymentId.trim() || null,
      });
      if (!data.success) throw new Error(data.message);
      toast.success('Marked as paid ✅');
      await load();
      setExpanded(null);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setMarking(null);
    }
  };

  const verifyReceipt = async (merchantTransactionId, action) => {
    const notes = action === 'REJECT'
      ? window.prompt('Reason for rejecting this receipt (optional):', '') || ''
      : '';
    try {
      const { data } = await API.post('/razorpay/verify-power-receipt', {
        merchantTransactionId, action, notes,
      });
      if (!data.success) throw new Error(data.message);
      toast.success(action === 'VERIFY' ? 'Marked as verified ✅' : 'Receipt rejected');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  // ── Delete: single + bulk ───────────────────────────────────────────────
  // Backend route deletes the power_event_bookings row(s) AND their paired
  // transactions row(s) — see controllers/powerBankTransferController.js
  const deleteBookings = async (merchantTransactionIds) => {
    setDeleting(true);
    try {
      const { data } = await API.post('/razorpay/delete-power-bookings', { merchantTransactionIds });
      if (!data.success) throw new Error(data.message);
      toast.success(`Deleted ${data.deleted} booking${data.deleted !== 1 ? 's' : ''}`);
      setSelected(prev => {
        const next = new Set(prev);
        merchantTransactionIds.forEach(id => next.delete(id));
        return next;
      });
      setExpanded(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const deleteOne = (b) => {
    if (!window.confirm(`Delete this booking permanently?\n\n${b.company_name} — ${b.merchant_transaction_id}\n\nThis cannot be undone.`)) return;
    deleteBookings([b.merchant_transaction_id]);
  };

  const deleteSelected = () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected booking${selected.size !== 1 ? 's' : ''} permanently?\n\nThis cannot be undone.`)) return;
    deleteBookings(Array.from(selected));
  };

  // ── Edit: open modal, day-row helpers, save modal ───────────────────────
  // Backend route: POST /razorpay/update-power-booking
  // handled by powerBankTransferController.updatePowerBooking
  const openEdit = (b) => setEditForm(bookingToEditForm(b));
  const closeEdit = () => { if (!savingEdit) setEditForm(null); };

  const updateDayRow = (key, field, value) => {
    setEditForm(prev => ({
      ...prev,
      days: prev.days.map(d => d._key === key ? { ...d, [field]: value } : d),
    }));
  };

  const addDayRow = () => setEditForm(prev => ({ ...prev, days: [...prev.days, newDayRow()] }));

  const removeDayRow = (key) => setEditForm(prev => ({
    ...prev,
    days: prev.days.length > 1 ? prev.days.filter(d => d._key !== key) : prev.days,
  }));

  const recalcFromDays = () => {
    setEditForm(prev => {
      const sub = prev.days.reduce((acc, d) => acc + (parseFloat(d.total) || 0), 0);
      const gst = prev.gstAmount !== '' && !isNaN(parseFloat(prev.gstAmount))
        ? parseFloat(prev.gstAmount)
        : Math.round(sub * 0.18 * 100) / 100;
      return { ...prev, subtotal: sub, gstAmount: gst, totalAmount: Math.round((sub + gst) * 100) / 100 };
    });
  };

  const saveEdit = async () => {
    if (!editForm) return;

    if (!editForm.boothNo?.trim() || !editForm.boothName?.trim() || !editForm.companyName?.trim()) {
      toast.error('Booth No, Booth Name and Company Name are required');
      return;
    }
    if (!editForm.totalAmount || isNaN(parseFloat(editForm.totalAmount))) {
      toast.error('Enter a valid total amount');
      return;
    }

    const cleanDays = editForm.days
      .filter(d => d.day !== '' || d.date !== '' || d.kv !== '' || d.total !== '')
      .map(d => ({
        day: d.day === '' ? null : Number(d.day),
        date: d.date || null,
        kv: d.kv === '' ? null : Number(d.kv),
        total: d.total === '' ? 0 : Number(d.total),
        ...(d.surcharge !== '' ? { surcharge: Number(d.surcharge) } : {}),
      }));

    setSavingEdit(true);
    try {
      const { data } = await API.post('/razorpay/update-power-booking', {
        merchantTransactionId: editForm.merchantTransactionId,

        boothNo: editForm.boothNo.trim(),
        boothName: editForm.boothName.trim(),

        companyDetails: {
          name: editForm.companyName.trim(),
          gst: editForm.companyGst.trim() || null,
          address: editForm.companyAddress.trim() || null,
        },
        email: editForm.companyEmail.trim() || null,
        phone: editForm.companyPhone.trim() || null,

        dayBreakdown: cleanDays,
        subtotal: editForm.subtotal === '' ? null : parseFloat(editForm.subtotal),
        gstAmount: editForm.gstAmount === '' ? null : parseFloat(editForm.gstAmount),
        totalAmount: parseFloat(editForm.totalAmount),
      });

      if (!data.success) throw new Error(data.message);
      toast.success('Booking updated ✅');
      setEditForm(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const filtered = bookings.filter(b => {
    const ok = filter === 'ALL' || b.payment_status === filter;
    const q  = search.toLowerCase();
    return ok && (!q ||
      b.company_name?.toLowerCase().includes(q) ||
      b.booth_no?.toLowerCase().includes(q) ||
      b.booth_name?.toLowerCase().includes(q) ||
      b.company_phone?.includes(q) ||
      b.company_email?.toLowerCase().includes(q) ||
      b.company_gst?.toLowerCase().includes(q) ||
      b.merchant_transaction_id?.toLowerCase().includes(q)
    );
  });

  // ── Delete: selection helpers (depend on `filtered`, so defined after it) ──
  const allVisibleSelected = filtered.length > 0 && filtered.every(b => selected.has(b.merchant_transaction_id));

  const toggleOne = (txnId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(txnId)) next.delete(txnId);
      else next.add(txnId);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach(b => next.delete(b.merchant_transaction_id));
      else filtered.forEach(b => next.add(b.merchant_transaction_id));
      return next;
    });
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };
  const s = stats || {};

  const [showExport, setShowExport] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportStatus, setExportStatus] = useState('ALL');
  const [exportFields, setExportFields] = useState(
    Object.fromEntries(EXPORT_FIELDS.map(f => [f.key, f.default]))
  );

  const toggleField = (key) => setExportFields(prev => ({ ...prev, [key]: !prev[key] }));
  const selectAllFields = () => setExportFields(Object.fromEntries(EXPORT_FIELDS.map(f => [f.key, true])));
  const selectDefaultFields = () => setExportFields(Object.fromEntries(EXPORT_FIELDS.map(f => [f.key, f.default])));

  const runExport = () => {
    let rows = bookings;

    if (exportStatus !== 'ALL') {
      rows = rows.filter(b => b.payment_status === exportStatus);
    }
    if (exportDateFrom) {
      const from = new Date(exportDateFrom + 'T00:00:00');
      rows = rows.filter(b => new Date(b.created_at) >= from);
    }
    if (exportDateTo) {
      const to = new Date(exportDateTo + 'T23:59:59');
      rows = rows.filter(b => new Date(b.created_at) <= to);
    }

    // dayDetail is rendered as dynamic per-day columns, not a normal field
    const activeFields = EXPORT_FIELDS.filter(f => exportFields[f.key] && f.key !== 'dayDetail');
    if (activeFields.length === 0 && !exportFields.dayDetail) {
      toast.error('Select at least one field to export');
      return;
    }
    if (rows.length === 0) {
      toast.error('No bookings match those filters');
      return;
    }

    const maxDays = exportFields.dayDetail
      ? Math.max(0, ...rows.map(b => dayBreakdownArray(b).length))
      : 0;

    // e.g. "2026-08-05" -> "05-08-2026"
    const ddmmyyyy = (dateStr) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr; // fallback if not parseable
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    };

    // Pick the date for each day-position from whichever booking has it first,
    // so the header can show one fixed date per day column.
    let dayHeaders = [];
    if (exportFields.dayDetail) {
      dayHeaders = Array.from({ length: maxDays }, (_, i) => {
        for (const b of rows) {
          const days = dayBreakdownArray(b);
          if (days[i]?.date) return `Day ${i + 1}- ${ddmmyyyy(days[i].date)}`;
        }
        return `Day ${i + 1}`;
      });
    }

    const sheetData = rows.map(b => {
      const row = {};
      activeFields.forEach(f => { row[f.label] = f.get(b); });

      if (exportFields.dayDetail) {
        const days = dayBreakdownArray(b);
        for (let i = 0; i < maxDays; i++) {
          const d = days[i];
          row[dayHeaders[i]] = d ? `${d.kv}k-${d.total}` : '';
        }
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const dayColWidths = exportFields.dayDetail
      ? Array.from({ length: maxDays }, () => ({ wch: 18 }))
      : [];
    worksheet['!cols'] = [
      ...activeFields.map(f => ({ wch: Math.max(f.label.length + 2, 14) })),
      ...dayColWidths,
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Power Bookings');

    const rangeLabel = exportDateFrom || exportDateTo
      ? `_${exportDateFrom || 'start'}_to_${exportDateTo || 'now'}`
      : '';
    const statusLabel = exportStatus !== 'ALL' ? `_${exportStatus}` : '';
    XLSX.writeFile(workbook, `power-payment-bookings${statusLabel}${rangeLabel}.xlsx`);

    toast.success(`Exported ${rows.length} booking${rows.length !== 1 ? 's' : ''}`);
    setShowExport(false);
  };

  return (
    <Layout>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="page-title" style={{ display:'flex', alignItems:'center', gap:10 }}>
            <FiZap style={{ color:'#f5a623' }} /> Power Event Bookings
          </h1>
          <p className="page-subtitle">Company-wise power bookings from the public form</p>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
            <button className="btn btn-outline btn-sm" onClick={load} disabled={loading || syncing}>
              <FiRefreshCw style={{ marginRight:6 }} />{loading ? 'Loading…' : 'Refresh'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setShowExport(true)}>
              <FiDownload style={{ marginRight:6 }} />Export Excel
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={syncAll}
              disabled={syncing || loading}
              style={{ background:'#00a86b', borderColor:'#00a86b' }}
            >
              <FiRefreshCw style={{ marginRight:6, animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
              {syncing ? 'Syncing with Razorpay…' : 'Sync All Statuses'}
            </button>
          </div>
          {syncMsg && (
            <span style={{ fontSize:12, color: syncMsg.startsWith('✅') ? '#00a86b' : syncMsg.startsWith('✓') ? '#6b6b80' : '#c0392b' }}>
              {syncMsg}
            </span>
          )}
        </div>
      </div>

      <div className="page-body fade-in">

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))', gap:16, marginBottom:28 }}>
          <StatCard icon={<FiZap />}         label="Total bookings"     value={s.total?.count ?? '—'} sub={s.total?.amount  ? fmt(s.total.amount) + ' requested' : null} color="#7c5cfc" />
          <StatCard icon={<FiCheckCircle />} label="Paid"               value={s.paid?.count  ?? '—'} sub={s.paid?.amount   ? fmt(s.paid.amount)  + ' collected' : null} color="#00a86b" />
          <StatCard icon={<FiClock />}       label="Awaiting payment"   value={s.pending?.count ?? '—'} color="#d4900a" />
          <StatCard icon={<FiXCircle />}     label="Failed / Cancelled" value={s.failed?.count  ?? '—'} color="#c0392b" />
        </div>

        <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ display:'flex', gap:6 }}>
            {['ALL','SUCCESS','PENDING','FAILED'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', border:'1px solid',
                borderColor: filter === f ? '#1a1a2e' : '#dcdce6',
                background:  filter === f ? '#1a1a2e' : '#fff',
                color:       filter === f ? '#fff'    : '#4a4a5e',
              }}>
                {f === 'ALL' ? 'All' : STATUS[f]?.label}
              </button>
            ))}
          </div>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search company, booth, phone, GST, email, txn ID…"
            style={{ flex:1, minWidth:220, padding:'8px 12px', borderRadius:10, border:'1px solid #dcdce6', fontSize:13, outline:'none' }}
          />
        </div>

        {/* ── Delete: bulk action bar — only shows once something's selected ── */}
        {selected.size > 0 && (
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap',
            background:'#1a1a2e', color:'#fff', borderRadius:12, padding:'12px 18px', marginBottom:16,
          }}>
            <span style={{ fontSize:13, fontWeight:600 }}>{selected.size} selected</span>
            <div style={{ display:'flex', gap:10 }}>
              <button
                onClick={() => setSelected(new Set())}
                style={{ background:'transparent', border:'1px solid rgba(255,255,255,.3)', color:'#fff', borderRadius:8, padding:'8px 14px', fontSize:12, fontWeight:600, cursor:'pointer' }}
              >
                Clear
              </button>
              <button
                onClick={deleteSelected}
                disabled={deleting}
                style={{ background:'#c0392b', border:'none', color:'#fff', borderRadius:8, padding:'8px 16px', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, opacity: deleting ? 0.6 : 1 }}
              >
                <FiTrash2 />{deleting ? 'Deleting…' : `Delete Selected (${selected.size})`}
              </button>
            </div>
          </div>
        )}

        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:48, color:'#b0b0c0', fontSize:14 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:48, color:'#b0b0c0', fontSize:14 }}>
              {bookings.length === 0 ? 'No power bookings yet.' : 'No results for this filter.'}
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#f8f8fc', borderBottom:'1px solid #ececf2' }}>
                    {/* ── Delete: select-all checkbox column ── */}
                    <th style={{ padding:'11px 14px', width:36 }}>
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} onClick={e => e.stopPropagation()} />
                    </th>
                    {['Date','Company','Booth','GST','Phone / Email','Power days','Total','Status','Verification','Link',''].map(h => (
                      <th key={h} style={{ padding:'11px 14px', textAlign:'left', fontWeight:700, color:'#6b6b80', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b, i) => {
                    const isOpen = expanded === b.merchant_transaction_id;
                    let dayCount = 0;
                    try { dayCount = (typeof b.day_breakdown === 'string' ? JSON.parse(b.day_breakdown) : b.day_breakdown)?.length || 0; } catch {}

                    return (
                      <>
                        <tr
                          key={b.merchant_transaction_id}
                          onClick={() => setExpanded(isOpen ? null : b.merchant_transaction_id)}
                          style={{ borderBottom:'1px solid #f0f0f5', cursor:'pointer', background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                        >
                          {/* ── Delete: per-row checkbox ── */}
                          <td style={{ padding:'11px 14px' }} onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selected.has(b.merchant_transaction_id)}
                              onChange={() => toggleOne(b.merchant_transaction_id)}
                            />
                          </td>

                          <td style={{ padding:'11px 14px', whiteSpace:'nowrap', color:'#6b6b80' }}>{fmtDt(b.created_at)}</td>

                          <td style={{ padding:'11px 14px', maxWidth:180 }}>
                            <div style={{ fontWeight:700, color:'#1a1a2e' }}>{b.company_name}</div>
                            {b.company_address && <div style={{ fontSize:11, color:'#b0b0c0', marginTop:2 }}>{b.company_address}</div>}
                          </td>

                          <td style={{ padding:'11px 14px', maxWidth:150 }}>
                            <div style={{ fontWeight:700, color:'#1a1a2e' }}>{b.booth_no || '—'}</div>
                            {b.booth_name && <div style={{ fontSize:11, color:'#b0b0c0', marginTop:2 }}>{b.booth_name}</div>}
                          </td>

                          <td style={{ padding:'11px 14px', color:'#4a4a5e', fontFamily:'monospace', fontSize:12 }}>
                            {b.company_gst || '—'}
                          </td>

                          <td style={{ padding:'11px 14px' }}>
                            <div style={{ fontWeight:600, color:'#1a1a2e' }}>{b.company_phone}</div>
                            {b.company_email && <div style={{ fontSize:11, color:'#b0b0c0' }}>{b.company_email}</div>}
                          </td>

                          <td style={{ padding:'11px 14px', textAlign:'center' }}>
                            <span style={{ background:'#f1f1f6', borderRadius:8, padding:'3px 10px', fontWeight:700, fontSize:12 }}>
                              {dayCount} day{dayCount !== 1 ? 's' : ''}
                            </span>
                          </td>

                          <td style={{ padding:'11px 14px', whiteSpace:'nowrap' }}>
                            <div style={{ fontWeight:800, color:'#1a1a2e' }}>{fmt(b.total_amount)}</div>
                            {b.subtotal != null && (
                              <div style={{ fontSize:11, color:'#b0b0c0' }}>
                                Sub {fmt(b.subtotal)} + GST {fmt(b.gst_amount)}
                              </div>
                            )}
                          </td>

                          <td style={{ padding:'11px 14px' }}><Chip status={b.payment_status} /></td>

                          <td style={{ padding:'11px 14px' }}><VerificationChip status={b.verification_status} /></td>

                          <td style={{ padding:'11px 14px' }}>
                            {b.payment_link ? (
                              <div style={{ display:'flex', gap:5 }}>
                                <button title="Copy" onClick={e => { e.stopPropagation(); copy(b.payment_link); }}
                                  style={{ background:'none', border:'1px solid #dcdce6', borderRadius:6, padding:'3px 8px', cursor:'pointer', color:'#4a4a5e' }}>
                                  <FiCopy size={12} />
                                </button>
                                <a href={b.payment_link} target="_blank" rel="noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  style={{ background:'none', border:'1px solid #dcdce6', borderRadius:6, padding:'3px 8px', cursor:'pointer', color:'#4a4a5e', textDecoration:'none', display:'inline-flex', alignItems:'center' }}>
                                  <FiExternalLink size={12} />
                                </a>
                              </div>
                            ) : '—'}
                          </td>

                          <td style={{ padding:'11px 14px', color:'#7c5cfc', fontWeight:700, fontSize:12 }}>
                            {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                          </td>
                        </tr>

                        {isOpen && (
                          <tr style={{ background:'#f8f8fc', borderBottom:'1px solid #e8e8f0' }}>
                            {/* ── colSpan bumped to 12 for the checkbox + booth columns ── */}
                            <td colSpan={12} style={{ padding:'16px 20px' }}>
                              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom: 10 }}>
                                <button
                                  className="btn btn-sm"
                                  onClick={(e) => { e.stopPropagation(); openEdit(b); }}
                                  style={{ background:'#7c5cfc', color:'#fff', border:'none' }}
                                >
                                  <FiEdit2 style={{ marginRight:4 }} />Edit booking
                                </button>
                              </div>
                              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:16 }}>

                                <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:12, padding:'14px 16px' }}>
                                  <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>Company details</div>
                                  {[
                                    ['Name',    b.company_name],
                                    ['Booth',   `${b.booth_no || '—'}${b.booth_name ? ' — ' + b.booth_name : ''}`],
                                    ['GST',     b.company_gst || '—'],
                                    ['Address', b.company_address || '—'],
                                    ['Email',   b.company_email || '—'],
                                    ['Phone',   b.company_phone],
                                  ].map(([lbl, val]) => (
                                    <div key={lbl} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'5px 0', borderBottom:'1px solid #f5f5fa', fontSize:13 }}>
                                      <span style={{ color:'#b0b0c0', fontWeight:600, flexShrink:0 }}>{lbl}</span>
                                      <span style={{ color:'#1a1a2e', fontWeight:600, textAlign:'right', wordBreak:'break-all' }}>{val}</span>
                                    </div>
                                  ))}
                                </div>

                                <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:12, padding:'14px 16px' }}>
                                  <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>Day-wise power</div>
                                  <DayBreakdown raw={b.day_breakdown} />
                                  <div style={{ marginTop:12, paddingTop:10, borderTop:'1px dashed #e8e8f0', display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, fontSize:12 }}>
                                    <div><div style={{ color:'#b0b0c0' }}>Subtotal</div><div style={{ fontWeight:700 }}>{fmt(b.subtotal)}</div></div>
                                    <div><div style={{ color:'#b0b0c0' }}>GST (18%)</div><div style={{ fontWeight:700 }}>{fmt(b.gst_amount)}</div></div>
                                    <div><div style={{ color:'#b0b0c0' }}>Total</div><div style={{ fontWeight:800, color:'#1a1a2e', fontSize:14 }}>{fmt(b.total_amount)}</div></div>
                                  </div>
                                </div>

                                <div style={{ background:'#fff', border:'1px solid #ececf2', borderRadius:12, padding:'14px 16px' }}>
                                  <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>Payment info</div>
                                  {[
                                    ['Status',      <Chip status={b.payment_status} />],
                                    ['Txn ID',      <span style={{ fontFamily:'monospace', fontSize:11 }}>{b.merchant_transaction_id}</span>],
                                    ['Razorpay ID', b.razorpay_payment_id || b.txn_razorpay_payment_id || '—'],
                                    ['Created',     fmtDt(b.created_at)],
                                  ].map(([lbl, val]) => (
                                    <div key={lbl} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'5px 0', borderBottom:'1px solid #f5f5fa', fontSize:13 }}>
                                      <span style={{ color:'#b0b0c0', fontWeight:600, flexShrink:0 }}>{lbl}</span>
                                      <span style={{ color:'#1a1a2e', fontWeight:600, textAlign:'right' }}>{val}</span>
                                    </div>
                                  ))}
                                  {b.payment_link && (
                                    <div style={{ marginTop:12, display:'flex', gap:8, flexWrap:'wrap' }}>
                                      <button className="btn btn-outline btn-sm" onClick={() => copy(b.payment_link)}><FiCopy style={{ marginRight:4 }} />Copy link</button>
                                      <a className="btn btn-outline btn-sm" href={b.payment_link} target="_blank" rel="noreferrer" style={{ textDecoration:'none' }}><FiExternalLink style={{ marginRight:4 }} />Open</a>
                                      {b.payment_status === 'PENDING' && (
                                        <button
                                          className="btn btn-sm"
                                          style={{ background:'#00a86b', color:'#fff', border:'none', opacity: marking === b.merchant_transaction_id ? 0.6 : 1 }}
                                          onClick={() => markAsPaid(b)}
                                          disabled={marking === b.merchant_transaction_id}
                                        >
                                          {marking === b.merchant_transaction_id ? 'Saving…' : '✅ Mark as Paid'}
                                        </button>
                                      )}
                                    </div>
                                  )}

                                  {b.receipt_path && (
                                    <div style={{ marginTop: 12 }}>
                                      <a
                                        className="btn btn-outline btn-sm"
                                        href={`${(process.env.REACT_APP_API_URL?.replace('/api','') || 'https://paymentsapi.steponexp.com').replace(/\/$/, '')}${b.receipt_path.startsWith('/') ? '' : '/'}${b.receipt_path}`}
                                        target="_blank" rel="noreferrer"
                                        style={{ textDecoration: 'none' }}
                                      >
                                        <FiExternalLink style={{ marginRight: 4 }} />View receipt
                                      </a>
                                      {b.utr_number && (
                                        <span style={{ marginLeft: 10, fontSize: 12, color: '#6b6b80' }}>
                                          UTR: <span style={{ fontFamily: 'monospace' }}>{b.utr_number}</span>
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {b.verification_status === 'SUBMITTED' && (
                                    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                                      <button
                                        className="btn btn-sm"
                                        style={{ background: '#00a86b', color: '#fff', border: 'none' }}
                                        onClick={() => verifyReceipt(b.merchant_transaction_id, 'VERIFY')}
                                      >
                                        ✅ Verify payment
                                      </button>
                                      <button
                                        className="btn btn-sm"
                                        style={{ background: '#c0392b', color: '#fff', border: 'none' }}
                                        onClick={() => verifyReceipt(b.merchant_transaction_id, 'REJECT')}
                                      >
                                        ❌ Reject
                                      </button>
                                    </div>
                                  )}

                                  {/* ── Delete + Edit: single-row action buttons ── */}
                                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e8e8f0', display:'flex', gap:8, flexWrap:'wrap' }}>
                                    <button
                                      className="btn btn-sm"
                                      style={{ background:'#7c5cfc', color:'#fff', border:'none' }}
                                      onClick={() => openEdit(b)}
                                    >
                                      <FiEdit2 style={{ marginRight:4 }} />Edit booking
                                    </button>
                                    <button
                                      className="btn btn-sm"
                                      style={{ background:'#c0392b', color:'#fff', border:'none', opacity: deleting ? 0.6 : 1 }}
                                      onClick={() => deleteOne(b)}
                                      disabled={deleting}
                                    >
                                      <FiTrash2 style={{ marginRight:4 }} />Delete booking
                                    </button>
                                  </div>
                                </div>

                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p style={{ marginTop:12, fontSize:12, color:'#b0b0c0' }}>
          Showing {filtered.length} of {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
        </p>

      </div>

      {showExport && (
        <div
          onClick={() => setShowExport(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,20,30,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16, padding: 24, width: 480,
              maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.25)',
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 4, fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>
              Export to Excel
            </h3>
            <p style={{ margin: 0, marginBottom: 18, fontSize: 12.5, color: '#6b6b80' }}>
              Choose a date range, status, and which columns to include.
            </p>

            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <label style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#4a4a5e' }}>
                From
                <input
                  type="date" value={exportDateFrom}
                  onChange={e => setExportDateFrom(e.target.value)}
                  style={{ width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 8, border: '1px solid #dcdce6', fontSize: 13 }}
                />
              </label>
              <label style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#4a4a5e' }}>
                To
                <input
                  type="date" value={exportDateTo}
                  onChange={e => setExportDateTo(e.target.value)}
                  style={{ width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 8, border: '1px solid #dcdce6', fontSize: 13 }}
                />
              </label>
            </div>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#4a4a5e', marginBottom: 16 }}>
              Status
              <select
                value={exportStatus}
                onChange={e => setExportStatus(e.target.value)}
                style={{ width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 8, border: '1px solid #dcdce6', fontSize: 13 }}
              >
                <option value="ALL">All statuses</option>
                <option value="SUCCESS">Paid</option>
                <option value="PENDING">Pending</option>
                <option value="FAILED">Failed</option>
              </select>
            </label>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#4a4a5e' }}>Columns to include</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={selectAllFields} style={{ fontSize: 11, fontWeight: 700, color: '#7c5cfc', background: 'none', border: 'none', cursor: 'pointer' }}>All</button>
                <button onClick={selectDefaultFields} style={{ fontSize: 11, fontWeight: 700, color: '#7c5cfc', background: 'none', border: 'none', cursor: 'pointer' }}>Default</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 10px', marginBottom: 20, maxHeight: 220, overflowY: 'auto', padding: '4px 2px' }}>
              {EXPORT_FIELDS.map(f => (
                <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#1a1a2e', cursor: 'pointer', padding: '4px 2px' }}>
                  <input
                    type="checkbox"
                    checked={!!exportFields[f.key]}
                    onChange={() => toggleField(f.key)}
                  />
                  {f.label}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowExport(false)}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={runExport}
                style={{ background: '#1a1a2e', borderColor: '#1a1a2e' }}
              >
                <FiDownload style={{ marginRight: 6 }} />Download Excel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit modal ─────────────────────────────────────────────────── */}
      {editForm && (
        <div
          onClick={closeEdit}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,20,30,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16, padding: 24, width: 700,
              maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.25)',
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 4, fontSize: 18, fontWeight: 800, color: '#1a1a2e', display:'flex', alignItems:'center', gap:8 }}>
              <FiEdit2 style={{ color:'#7c5cfc' }} /> Edit Power Booking
            </h3>
            <p style={{ margin: 0, marginBottom: 18, fontSize: 12.5, color: '#6b6b80', fontFamily:'monospace' }}>
              {editForm.merchantTransactionId}
            </p>

            <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Booth</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <Field label="Booth No" value={editForm.boothNo} onChange={v => setEditField('boothNo', v)} />
              <Field label="Booth Name" value={editForm.boothName} onChange={v => setEditField('boothName', v)} />
            </div>

            <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Company details</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <Field label="Company Name" value={editForm.companyName} onChange={v => setEditField('companyName', v)} span />
              <Field label="GST Number" value={editForm.companyGst} onChange={v => setEditField('companyGst', v)} />
              <Field label="Address" value={editForm.companyAddress} onChange={v => setEditField('companyAddress', v)} />
              <Field label="Phone" value={editForm.companyPhone} onChange={v => setEditField('companyPhone', v)} />
              <Field label="Email" value={editForm.companyEmail} onChange={v => setEditField('companyEmail', v)} />
            </div>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em' }}>Day-wise power</div>
              <button
                onClick={addDayRow}
                style={{ fontSize: 11, fontWeight: 700, color: '#7c5cfc', background: 'none', border: 'none', cursor: 'pointer', display:'flex', alignItems:'center', gap:4 }}
              >
                <FiPlus size={12} />Add day
              </button>
            </div>

            <div style={{ marginBottom: 12 }}>
              {editForm.days.map((d) => (
                <div key={d._key} style={{ display:'grid', gridTemplateColumns:'0.7fr 1.2fr 0.8fr 1fr 1fr auto', gap:8, marginBottom:8, alignItems:'end' }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#4a4a5e' }}>
                    Day
                    <input type="number" value={d.day} onChange={e => updateDayRow(d._key, 'day', e.target.value)} style={fieldStyle} />
                  </label>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#4a4a5e' }}>
                    Date
                    <input type="text" placeholder="e.g. 12 Feb 2026" value={d.date} onChange={e => updateDayRow(d._key, 'date', e.target.value)} style={fieldStyle} />
                  </label>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#4a4a5e' }}>
                    kV
                    <input type="number" value={d.kv} onChange={e => updateDayRow(d._key, 'kv', e.target.value)} style={fieldStyle} />
                  </label>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#4a4a5e' }}>
                    Day Total (₹)
                    <input type="number" value={d.total} onChange={e => updateDayRow(d._key, 'total', e.target.value)} style={fieldStyle} />
                  </label>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#4a4a5e' }}>
                    3-way surcharge
                    <input type="number" value={d.surcharge} onChange={e => updateDayRow(d._key, 'surcharge', e.target.value)} style={fieldStyle} />
                  </label>
                  <button
                    onClick={() => removeDayRow(d._key)}
                    disabled={editForm.days.length <= 1}
                    title="Remove day"
                    style={{
                      background: 'none', border: '1px solid #dcdce6', borderRadius: 8, padding: '8px 10px',
                      color: '#c0392b', cursor: editForm.days.length <= 1 ? 'not-allowed' : 'pointer', opacity: editForm.days.length <= 1 ? 0.4 : 1,
                    }}
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#b0b0c0', textTransform:'uppercase', letterSpacing:'.05em' }}>Amounts</div>
              <button
                onClick={recalcFromDays}
                style={{ fontSize: 11, fontWeight: 700, color: '#7c5cfc', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Recalculate from days (18% GST)
              </button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:20 }}>
              <Field label="Subtotal (₹)" type="number" value={editForm.subtotal} onChange={v => setEditField('subtotal', v)} />
              <Field label="GST Amount (₹)" type="number" value={editForm.gstAmount} onChange={v => setEditField('gstAmount', v)} />
              <Field label="Total Amount (₹)" type="number" value={editForm.totalAmount} onChange={v => setEditField('totalAmount', v)} />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={closeEdit} disabled={savingEdit}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={saveEdit}
                disabled={savingEdit}
                style={{ background: '#7c5cfc', borderColor: '#7c5cfc', opacity: savingEdit ? 0.6 : 1 }}
              >
                {savingEdit ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}